import { useState } from 'react';
import { AgentAvatar } from '../AgentAvatar';
import { uniqueNamesGenerator, adjectives, animals } from 'unique-names-generator';

function generateAgentName() {
  const slug = uniqueNamesGenerator({ dictionaries: [adjectives, animals], separator: '-', length: 2, style: 'lowerCase' }).slice(0, 16);
  const arr = new Uint8Array(3);
  crypto.getRandomValues(arr);
  const suffix = Array.from(arr).map(b => b.toString(16).padStart(2, '0')).join('');
  return `${slug}-${suffix}`;
}

/**
 * Onboarding Step 1: Meet Your Agent.
 * Per wireframe OnboardingB — agent avatar + name + capabilities list.
 */
export function OnboardingStep1({ config, portfolioId, jwtToken, onComplete }) {
  const [agentName, setAgentName] = useState(() => generateAgentName());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleRegenerate = () => setAgentName(generateAgentName());

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      // Create agent
      const agentRes = await fetch(`${config.apiBaseUrl}/agents`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${jwtToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: agentName, max_history_messages: 10 }),
      });
      if (!agentRes.ok) throw new Error(`Create agent failed (${agentRes.status})`);
      const agent = await agentRes.json();

      // Assign to portfolio
      const assignRes = await fetch(`${config.apiBaseUrl}/portfolio/${portfolioId}/agent`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${jwtToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ agent_id: agent.id, role: 'primary' }),
      });
      if (!assignRes.ok) throw new Error(`Assign agent failed (${assignRes.status})`);

      onComplete({ agentId: agent.id, agentName: agent.name });
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 items-center text-center py-2">
      <AgentAvatar name={agentName} seed={agentName} size={80} />
      <div className="text-xl text-white">
        meet <span className="font-bold">{agentName}</span>
      </div>
      <p className="text-sm text-slate-400 max-w-sm">
        Your autonomous trading agent. It scans markets, runs research, and recommends trades. You approve every execution.
      </p>

      <div className="flex gap-2">
        <button onClick={handleRegenerate} className="px-3 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded text-xs">
          🔄 regenerate
        </button>
      </div>

      <div className="w-full bg-slate-800 border border-slate-700 rounded-lg p-4 text-left space-y-2 mt-2">
        <div className="text-xs text-slate-400 uppercase tracking-wide font-mono">What your agent can do</div>
        <div className="text-sm text-slate-200"><span className="text-green-400">✓</span> Scan trending tokens every 5 min</div>
        <div className="text-sm text-slate-200"><span className="text-green-400">✓</span> Run research on any strategy bucket</div>
        <div className="text-sm text-slate-200"><span className="text-green-400">✓</span> Generate recommendations with risk rules</div>
        <div className="text-sm text-slate-200"><span className="text-red-400">✗</span> Execute trades without your approval</div>
      </div>

      {error && <p className="text-red-400 text-xs">{error}</p>}

      <hr className="border-slate-700 border-dashed w-full" />

      <div className="flex items-center justify-between w-full">
        <span className="text-xs text-slate-500">you can skip — a default agent will be created</span>
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className={`px-4 py-2 rounded text-sm font-medium text-white ${
            submitting ? 'bg-cyan-900 opacity-50' : 'bg-cyan-600 hover:bg-cyan-500'
          }`}
        >
          {submitting ? 'Creating…' : "let's go →"}
        </button>
      </div>
    </div>
  );
}
